import { useState, useEffect } from 'react';
import { TopNavigation } from './TopNavigation';
import { BillingAdminDashboard } from './BillingAdminDashboard';
import { Building2 } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface BillingAdminViewProps {
  accessToken: string;
  currentUser: any;
  onLogout: () => void;
}

export function BillingAdminView({ accessToken, currentUser, onLogout }: BillingAdminViewProps) {
  const [firm, setFirm] = useState<any>(null);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFirmData();
    loadAllUsers();
  }, [accessToken, currentUser]);

  const loadFirmData = async () => {
    try {
      const firmId = currentUser.firmId || currentUser.consultingFirmId;
      if (!firmId) {
        console.error('[BillingAdminView] No firmId found for user');
        return;
      }

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consulting-firms/${firmId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFirm(data);
      } else {
        console.error('[BillingAdminView] Failed to load firm data');
      }
    } catch (error) {
      console.error('[BillingAdminView] Error loading firm data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/users`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAllUsers(data);
      }
    } catch (error) {
      console.error('[BillingAdminView] Error loading users:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopNavigation
          user={{
            id: currentUser.id,
            name: currentUser.name || currentUser.email?.split('@')[0] || 'Billing Admin',
            email: currentUser.email,
            role: currentUser.role || 'billingAdmin',
            avatarUrl: currentUser.avatarUrl,
            firmId: currentUser.firmId || currentUser.consultingFirmId,
            firmName: currentUser.firmName,
            isDemo: currentUser.isDemo,
          }}
          companyId={null}
          companyName={null}
          accessToken={accessToken}
          onLogout={onLogout}
        >
          <div className="flex items-center gap-3">
            <Building2 className="size-6 text-blue-600" />
            <div>
              <h1 className="text-xl font-bold">Billing & Subscription</h1>
              <p className="text-xs text-muted-foreground">Loading...</p>
            </div>
          </div>
        </TopNavigation>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-2xl mb-2">Loading...</div>
              <div className="text-gray-600">Fetching billing information</div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!firm) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopNavigation
          user={{
            id: currentUser.id,
            name: currentUser.name || currentUser.email?.split('@')[0] || 'Billing Admin',
            email: currentUser.email,
            role: currentUser.role || 'billingAdmin',
            avatarUrl: currentUser.avatarUrl,
            firmId: currentUser.firmId || currentUser.consultingFirmId,
            firmName: currentUser.firmName,
            isDemo: currentUser.isDemo,
          }}
          companyId={null}
          companyName={null}
          accessToken={accessToken}
          onLogout={onLogout}
        >
          <div className="flex items-center gap-3">
            <Building2 className="size-6 text-blue-600" />
            <div>
              <h1 className="text-xl font-bold">Billing & Subscription</h1>
              <p className="text-xs text-muted-foreground">Error loading firm</p>
            </div>
          </div>
        </TopNavigation>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-xl mb-2">Firm Not Found</h2>
            <p className="text-gray-600">Unable to load your consulting firm information.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <TopNavigation
        user={{
          id: currentUser.id,
          name: currentUser.name || currentUser.email?.split('@')[0] || 'Billing Admin',
          email: currentUser.email,
          role: currentUser.role || 'billingAdmin',
          avatarUrl: currentUser.avatarUrl,
          firmId: currentUser.firmId || currentUser.consultingFirmId,
          firmName: currentUser.firmName || firm.name,
          isDemo: currentUser.isDemo,
        }}
        companyId={null}
        companyName={null}
        accessToken={accessToken}
        onLogout={onLogout}
      >
        <div className="flex items-center gap-3">
          <Building2 className="size-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-bold">Billing & Subscription</h1>
            <p className="text-xs text-muted-foreground">{firm.name}</p>
          </div>
        </div>
      </TopNavigation>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <BillingAdminDashboard
          currentUser={currentUser}
          firm={firm}
          allUsers={allUsers}
          accessToken={accessToken}
          onDataChanged={() => {
            loadFirmData();
            loadAllUsers();
          }}
        />
      </main>
    </div>
  );
}
